import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePicker, toaster } from 'rsuite'
import isAfter from 'date-fns/isAfter'
import moment from 'moment'
import { saveAs } from 'file-saver'
import { Colors } from 'Theme'
import { IFButton, IFModal, IFText, IFToastMessage } from 'Components'
import { transactionsApiService } from 'Services/ApiService'
import Styles from './UserBalancesReport.module.css'

// Lets an admin download a CSV snapshot of every user's balance as it stood at
// close of a chosen day, reconstructed server-side from the transaction ledger
// (billingAccountBalance.before/after). This replaced the monthly email sent
// to finance, which reported balances only as of the 1st of each month.
const UserBalancesReport = () => {
  const { t } = useTranslation()
  const modalRef = useRef()
  const [selectedDate, setSelectedDate] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = () => {
    if (!selectedDate) return
    setIsDownloading(true)
    const formattedDate = moment(selectedDate).format('YYYY-MM-DD')

    transactionsApiService({
      url: '/balancesReport',
      method: 'POST',
      data: { date: formattedDate },
      responseType: 'blob',
    })
      .then((res) => {
        const csvBlob = new Blob([res.data], { type: 'text/csv' })
        saveAs(csvBlob, `user-balances-${formattedDate}.csv`)
        modalRef.current.dismiss()
      })
      .catch((err) => {
        toaster.push(
          <IFToastMessage
            type="error"
            text={err.message ? err.message : err.statusText}
          />,
        )
      })
      .finally(() => setIsDownloading(false))
  }

  return (
    <>
      <IFButton
        size="md"
        color={Colors.primary}
        isFill={false}
        isLoading={false}
        text={t('TransactionsPage.UserBalancesModal.ButtonLabel')}
        onClick={() => modalRef.current.show()}
      />
      <IFModal
        title={t('TransactionsPage.UserBalancesModal.Title')}
        open={false}
        maxWidth="xs"
        fullWidth
        ref={modalRef}
        onDismiss={() => setSelectedDate(null)}
      >
        <div className={Styles.Content}>
          <IFText className={Styles.Label}>
            {t('TransactionsPage.UserBalancesModal.Description')}
          </IFText>
          <DatePicker
            className={Styles.DatePicker}
            block
            oneTap
            cleanable={false}
            format="yyyy-MM-dd"
            value={selectedDate}
            onChange={setSelectedDate}
            shouldDisableDate={(date) => isAfter(date, new Date())}
            placeholder={t('TransactionsPage.UserBalancesModal.SelectDate')}
          />
          <div className={Styles.ButtonRow}>
            <IFButton
              className={Styles.DownloadButton}
              color={Colors.primary}
              isFill={true}
              isDead={!selectedDate}
              isLoading={isDownloading}
              text={t('TransactionsPage.UserBalancesModal.Download')}
              onClick={handleDownload}
            />
          </div>
        </div>
      </IFModal>
    </>
  )
}

export default UserBalancesReport
